/**
 * Employee directory Excel export.
 *
 * Builds a structured multi-sheet workbook with SheetJS (`xlsx`):
 *   - "Employees"  — one row per employee, one column per field, grouped by
 *                    the section toggles the user picked.
 *   - Detail sheets (Detailed mode only) for the repeating groups —
 *     Academic, Work Experience, Nominees, References, Insurances and
 *     Family Members — each row keyed by Employee ID / Name / E-Mail so the
 *     sheets can be joined/filtered in Excel.
 *
 * Section keys intentionally match `EXPORT_SECTIONS` from the PDF schema so
 * the same dialog options drive both exporters. Document "images" become URL
 * columns here (Excel can't embed the files).
 *
 * Public API:
 *   exportEmployeesXlsx({ filters, searchTerm, includeResigned,
 *                         includeInactive, options,
 *                         onProgress, onError, onSuccess })
 */
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';

import {
    fetchCalendars,
    fetchEmployeesForExport,
    secureFileViewerLink
} from './employeeExportApi';
import { normalizeEmployee, fullNameOf } from './employeePdfSchema';

/* ------------------------------------------------------------------ */
/* Value helpers                                                       */
/* ------------------------------------------------------------------ */
const fmtDate = (v) => {
    if (!v) return '';
    const d = dayjs(v);
    return d.isValid() ? d.format('YYYY-MM-DD') : '';
};

const fmtYear = (v) => {
    if (!v) return '';
    const d = dayjs(v);
    return d.isValid() ? d.format('YYYY') : String(v);
};

const personName = (p) =>
    p
        ? [p.firstname, p.middlename, p.lastname]
              .filter(Boolean)
              .join(' ')
              .replace(/\s+/g, ' ')
              .trim()
        : '';

const joinAddress = (a) =>
    a
        ? [a.address, a.thana, a.district, a.city, a.country]
              .filter(Boolean)
              .join(', ')
        : '';

const joinList = (v) => (Array.isArray(v) ? v.filter(Boolean).join(', ') : v);

const yesNo = (v) => (v ? 'Yes' : 'No');

const text = (v) => (v === undefined || v === null ? '' : String(v));

const employmentStatus = (e) =>
    e.resignationDate && dayjs(e.resignationDate).isBefore(dayjs())
        ? 'Resigned'
        : 'Active';

/* ------------------------------------------------------------------ */
/* "Employees" sheet — column groups per section                       */
/* col(label, valueFn, includeInSummary)                               */
/* ------------------------------------------------------------------ */
const col = (label, value, s = false) => ({ label, value, s });

/**
 * A column holding a secured-document link. The cell shows (and links to)
 * the in-app /file-viewer URL, which serves the file with the viewer's own
 * credentials — permanent and permission-safe, unlike raw storage paths.
 */
const linkCol = (label, path, s = false) => ({
    label,
    value: (record) => secureFileViewerLink(path(record)),
    s,
    link: true
});

/** Identity columns — always present so every sheet/row is identifiable. */
const IDENTITY_COLUMNS = [
    col('Employee ID', (e) => text(e.employeeId), true),
    col('Full Name', (e) => fullNameOf(e), true),
    col('Official E-Mail', (e) => text(e.user?.email || e.contact?.officialEmail), true)
];

const MAIN_COLUMNS = {
    general: ({ calendarMap }) => [
        col('Status', (e) => employmentStatus(e), true),
        col('Account Active', (e) => yesNo(e.user?.accountIsActive !== false), true),
        col('Gender', (e) => text(e.gender), true),
        col('Date of Birth', (e) => fmtDate(e.dateOfBirth), true),
        col('Blood Group', (e) => text(e.bloodGroup), true),
        col('Joining Date', (e) => fmtDate(e.joiningDate), true),
        col('Designation', (e) => text(e.designation?.name), true),
        col('Department', (e) => text(e.department?.name), true),
        col('Branch', (e) => text(e.branch?.name), true),
        col('Name (In Bengali)', (e) => text(e.nameInBengali)),
        col('Initial', (e) => text(e.initial)),
        col('Place of Birth', (e) => text(e.placeOfBirth)),
        col('Religion', (e) => text(e.religion)),
        col('Marital Status', (e) => text(e.maritalStatus)),
        col('Employee Category', (e) => text(e.employeeCategory?.name)),
        col('Shift', (e) => text(e.shift?.name || e.shiftName)),
        col('Reporting Manager', (e) => text(e.reportingManager)),
        col('Work Calendar', (e) =>
            text(calendarMap[e.calenderId] || e.calenderId)
        ),
        col('Last Working Day', (e) => fmtDate(e.resignationDate)),
        col('Terminated', (e) =>
            typeof e.isTerminated === 'boolean' ? yesNo(e.isTerminated) : ''
        ),
        col('Resignation Note', (e) => text(e.description))
    ],
    contact: () => [
        col('Personal E-Mail', (e) => text(e.contact?.personalEmail), true),
        col('Mobile No', (e) => text(e.contact?.mobileNumber), true),
        col(
            'Emergency Contact No',
            (e) => text(e.contact?.emergencyContact?.contact),
            true
        ),
        col('Present Address', (e) => joinAddress(e.contact?.presentAddress)),
        col('Present Mobile No', (e) =>
            text(e.contact?.presentAddress?.phoneNumber)
        ),
        col('Permanent Address', (e) =>
            joinAddress(e.contact?.permanentAddress)
        ),
        col('Permanent Mobile No', (e) =>
            text(e.contact?.permanentAddress?.phoneNumber)
        ),
        col('Emergency Contact Name', (e) =>
            personName(e.contact?.emergencyContact)
        ),
        col('Emergency Relation', (e) =>
            text(e.contact?.emergencyContact?.relation)
        ),
        col('Emergency Address', (e) =>
            text(e.contact?.emergencyContact?.address?.address)
        )
    ],
    family: () => [
        col('Is Married', (e) => yesNo(e.family?.isMarried), true),
        col('Spouse Name', (e) => personName(e.family?.spouse), true),
        col(
            'Children Count',
            (e) => (Array.isArray(e.family?.children) ? e.family.children.length : 0),
            true
        ),
        col('Marriage Date', (e) => fmtDate(e.family?.marriageDate)),
        col('Spouse Contact', (e) => text(e.family?.spouse?.contact)),
        col('Spouse Occupation', (e) => text(e.family?.spouse?.occupation)),
        col('Spouse Education', (e) =>
            text(e.family?.spouse?.education?.institutions?.[0]?.qualification)
        ),
        col("Father's Name", (e) => personName(e.father)),
        col("Father's Name (In Bengali)", (e) => text(e.father?.nameInBengali)),
        col("Father's Occupation", (e) => text(e.father?.occupation)),
        col("Mother's Name", (e) => personName(e.mother)),
        col("Mother's Name (In Bengali)", (e) => text(e.mother?.nameInBengali)),
        col("Mother's Occupation", (e) => text(e.mother?.occupation)),
        col('Related Employees Count', (e) => e.relatedEmployees.length)
    ],
    identification: ({ parts }) => [
        col('Nationality', (e) => text(e.identification?.nationality), true),
        col('NID Number', (e) => text(e.identification?.nidNumber), true),
        col(
            'Passport Number',
            (e) => text(e.identification?.passport?.number),
            true
        ),
        col('TIN', (e) => text(e.identification?.tin)),
        col('Driving License Number', (e) =>
            text(e.identification?.drivingLicenseNumber)
        ),
        col('Passport Issue Place', (e) =>
            text(e.identification?.passport?.issuePlace)
        ),
        col('Passport Issue Date', (e) =>
            fmtDate(e.identification?.passport?.issueDate)
        ),
        col('Passport Expiry Date', (e) =>
            fmtDate(e.identification?.passport?.expiryDate)
        ),
        // Document links — Excel can't embed images, so ship viewer links.
        parts.photograph &&
            linkCol('Photograph Link', (e) => e.identification?.photograph, true),
        parts.signature &&
            linkCol('Signature Link', (e) => e.identification?.signature, true),
        parts.nid && linkCol('NID Card Link', (e) => e.identification?.nid, true),
        parts.etin && linkCol('E-TIN Link', (e) => e.identification?.etin, true)
    ],
    bank: () => [
        col('Bank Name', (e) => text(e.bank?.name), true),
        col('Bank Branch', (e) => text(e.bank?.branch), true),
        col('A/C No', (e) => text(e.bank?.accountNumber), true)
    ],
    academic: () => [
        col(
            'Academic Records',
            (e) =>
                Array.isArray(e.academic?.institutions)
                    ? e.academic.institutions.length
                    : 0,
            true
        )
    ],
    workExperience: () => [
        col('Work Experiences', (e) => e.workExperiences.length, true)
    ],
    nominees: () => [col('Nominees', (e) => e.nominees.length, true)],
    references: () => [col('References', (e) => e.references.length, true)],
    insurances: () => [col('Insurances', (e) => e.insurances.length, true)]
};

const buildMainColumns = ({ sections, detailed, parts, calendarMap }) => {
    const columns = [...IDENTITY_COLUMNS];
    Object.entries(MAIN_COLUMNS).forEach(([key, factory]) => {
        if (!sections[key]) return;
        const group = factory({ parts, calendarMap }).filter(Boolean);
        columns.push(...(detailed ? group : group.filter((c) => c.s)));
    });
    return columns;
};

/* ------------------------------------------------------------------ */
/* Detail sheets (Detailed mode only)                                  */
/* Each sheet: identity columns + '#' + its own columns; one row per   */
/* record; employees without records simply have no rows.              */
/* ------------------------------------------------------------------ */
const DETAIL_SHEETS = [
    {
        section: 'academic',
        name: 'Academic',
        records: (e) =>
            Array.isArray(e.academic?.institutions)
                ? e.academic.institutions
                : [],
        columns: () => [
            col('Qualification', (r) => text(r.qualification)),
            col('Discipline / Department', (r) => text(r.department)),
            col('Institute Name', (r) => text(r.name)),
            col('Institute / Board', (r) => text(r.board)),
            col('Passing Year', (r) => fmtYear(r.passingYear)),
            col('Result (Marks/CGPA)', (r) => text(r.result))
        ]
    },
    {
        section: 'workExperience',
        name: 'Work Experience',
        records: (e) => e.workExperiences,
        columns: () => [
            col('Company Name', (r) => text(r.companyName)),
            col('Location', (r) => text(r.location)),
            col('Designation', (r) => text(r.designation)),
            col('Years of Experience', (r) => text(r.yearsOfExperience)),
            col('Skills', (r) => text(joinList(r.skills))),
            col('Contributions', (r) => text(joinList(r.contributions))),
            col('Achievements', (r) => text(joinList(r.achievements)))
        ]
    },
    {
        section: 'nominees',
        name: 'Nominees',
        records: (e) => e.nominees,
        columns: ({ parts }) =>
            [
                col('Nomination Purpose', (r) => text(r.nominationPurpose)),
                col('Nomination Date', (r) => fmtDate(r.nominationDate)),
                col('Nominee Name', (r) => text(r.information?.firstname)),
                col('Relation with Employee', (r) => text(r.information?.relation)),
                col('Percentage', (r) => text(r.percentage)),
                col('Date of Birth', (r) => fmtDate(r.dateOfBirth)),
                col('Occupation', (r) => text(r.information?.occupation)),
                col('Address', (r) => text(r.information?.address?.address)),
                col('Contact No', (r) => text(r.information?.contact)),
                col('E-Mail', (r) => text(r.information?.email)),
                parts.nomineeDocs &&
                    linkCol('Photograph Link', (r) => r.photograph),
                parts.nomineeDocs &&
                    linkCol('Signature Link', (r) => r.signature)
            ].filter(Boolean)
    },
    {
        section: 'references',
        name: 'References',
        records: (e) => e.references,
        columns: () => [
            col('Name', (r) => text(r.firstname)),
            col('Occupation', (r) => text(r.occupation)),
            col('Relation with Employee', (r) => text(r.relation)),
            col('Contact No', (r) => text(r.contact)),
            col('E-Mail', (r) => text(r.email))
        ]
    },
    {
        section: 'insurances',
        name: 'Insurances',
        records: (e) => e.insurances,
        columns: ({ parts }) =>
            [
                col('Family Member Name', (r) => text(r.information?.firstname)),
                col('Nature of Insurance', (r) => text(r.natureOfInsurance)),
                col('Insurance Amount', (r) => text(r.insuranceAmount)),
                col('Relation with Employee', (r) => text(r.information?.relation)),
                col('Registration Date', (r) => fmtDate(r.registrationDate)),
                col('Date of Birth', (r) => fmtDate(r.dateOfBirth)),
                col('Occupation', (r) => text(r.information?.occupation)),
                parts.insuranceDocs &&
                    linkCol('Photograph Link', (r) => r.photograph)
            ].filter(Boolean)
    },
    {
        section: 'family',
        name: 'Family Members',
        records: (e) => [
            ...(Array.isArray(e.family?.children) ? e.family.children : []).map(
                (child) => ({
                    type: 'Child',
                    name: personName(child) || text(child.firstname),
                    detail: text(child.gender)
                })
            ),
            ...e.relatedEmployees.map((r) => ({
                type: 'Related Employee',
                name: text(r.employeeId),
                detail: text(r.relationship || r.relation)
            }))
        ],
        columns: () => [
            col('Type', (r) => r.type),
            col('Name / Employee ID', (r) => r.name),
            col('Gender / Relationship', (r) => r.detail)
        ]
    }
];

/* ------------------------------------------------------------------ */
/* Workbook assembly                                                   */
/* ------------------------------------------------------------------ */
const buildSheet = (headers, rows, linkColumns = []) => {
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    ws['!cols'] = headers.map((header, c) => {
        let wch = String(header).length;
        rows.forEach((row) => {
            const v = row[c];
            if (v !== undefined && v !== null)
                wch = Math.max(wch, String(v).length);
        });
        return { wch: Math.min(50, Math.max(10, wch + 2)) };
    });
    ws['!autofilter'] = {
        ref: XLSX.utils.encode_range({
            s: { r: 0, c: 0 },
            e: { r: rows.length, c: Math.max(0, headers.length - 1) }
        })
    };
    // Turn link-column cells into real clickable hyperlinks.
    linkColumns.forEach((c) => {
        rows.forEach((row, r) => {
            const target = row[c];
            if (!target) return;
            const addr = XLSX.utils.encode_cell({ r: r + 1, c });
            if (ws[addr]) ws[addr].l = { Target: String(target) };
        });
    });
    return ws;
};

const linkColumnIndexes = (columns, offset = 0) =>
    columns.reduce((acc, column, index) => {
        if (column.link) acc.push(offset + index);
        return acc;
    }, []);

const buildWorkbook = (employees, { sections, detailed, parts, calendarMap }) => {
    const wb = XLSX.utils.book_new();

    // Main sheet — one row per employee.
    const mainColumns = buildMainColumns({
        sections,
        detailed,
        parts,
        calendarMap
    });
    const mainRows = employees.map((e) =>
        mainColumns.map((column) => column.value(e))
    );
    XLSX.utils.book_append_sheet(
        wb,
        buildSheet(
            mainColumns.map((column) => column.label),
            mainRows,
            linkColumnIndexes(mainColumns)
        ),
        'Employees'
    );

    // Detail sheets — Detailed mode only.
    if (detailed) {
        DETAIL_SHEETS.forEach((sheet) => {
            if (!sections[sheet.section]) return;
            const columns = sheet.columns({ parts });
            const rows = [];
            employees.forEach((e) => {
                sheet.records(e).forEach((record, index) => {
                    rows.push([
                        ...IDENTITY_COLUMNS.map((column) => column.value(e)),
                        index + 1,
                        ...columns.map((column) => column.value(record))
                    ]);
                });
            });
            if (!rows.length) return;
            XLSX.utils.book_append_sheet(
                wb,
                buildSheet(
                    [
                        ...IDENTITY_COLUMNS.map((column) => column.label),
                        '#',
                        ...columns.map((column) => column.label)
                    ],
                    rows,
                    // identity columns + the '#' column precede the data cols
                    linkColumnIndexes(columns, IDENTITY_COLUMNS.length + 1)
                ),
                sheet.name
            );
        });
    }

    return wb;
};

/* ------------------------------------------------------------------ */
/* Public orchestrator                                                 */
/* ------------------------------------------------------------------ */
export async function exportEmployeesXlsx({
    filters = {},
    searchTerm = '',
    includeResigned = false,
    includeInactive = false,
    options = {},
    onProgress,
    onError,
    onSuccess
} = {}) {
    const detailed = options.detail !== 'summary';
    const sections = options.sections || {};
    const parts = options.includeImages ? options.imageParts || {} : {};
    const progress = (message) => onProgress && onProgress(message);

    try {
        progress('Fetching employees…');
        const { employees: rawEmployees } = await fetchEmployeesForExport(
            { filters, searchTerm, includeResigned, includeInactive },
            (done, total) => progress(`Fetching employees ${done}/${total}…`)
        );
        if (!rawEmployees.length) {
            throw new Error('No employees match the selected filters.');
        }
        const employees = rawEmployees.map(normalizeEmployee);

        // One calendars lookup serves every employee.
        let calendarMap = {};
        if (sections.general) {
            progress('Loading work calendars…');
            const calenders = await fetchCalendars();
            calendarMap = Object.fromEntries(
                calenders.map((c) => [c.id, c.name])
            );
        }

        progress('Building Excel workbook…');
        const wb = buildWorkbook(employees, {
            sections,
            detailed,
            parts,
            calendarMap
        });
        XLSX.writeFile(
            wb,
            `employee-directory-${dayjs().format('YYYY-MM-DD')}.xlsx`
        );
        onSuccess && onSuccess();
    } catch (err) {
        const msg =
            err?.response?.data?.message ||
            err?.message ||
            'Failed to generate the employee directory Excel file.';
        onError && onError(msg);
    }
}

export default exportEmployeesXlsx;
